import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { TooltipTrigger, type TooltipPlacement } from '../Tooltip/TooltipTrigger';
import './TruncatedText.css';

/**
 * True when single-line ellipsis is (or should be) showing. A 1px slop
 * hides the tooltip for names that CSS still clips, so any overflow counts.
 * Chromium can also report equal integer scroll/client widths while still
 * painting ellipsis; the Range width catches that.
 */
export function isTextOverflowing(el: HTMLElement): boolean {
  if (el.clientWidth === 0 && el.scrollWidth === 0) return false;
  if (el.scrollWidth > el.clientWidth) return true;
  if (typeof document === 'undefined' || typeof document.createRange !== 'function') {
    return false;
  }
  try {
    const range = document.createRange();
    range.selectNodeContents(el);
    return range.getBoundingClientRect().width > el.getBoundingClientRect().width + 0.5;
  } catch {
    return false;
  }
}

export type TruncatedTextProps = {
  /** The text content to display and use as tooltip. */
  text?: string;
  /** Override the tooltip content (defaults to `text`). */
  tooltipText?: string;
  /** Maximum width before truncation (CSS value or number for px). */
  maxWidth?: number | string;
  /** When true, always show tooltip regardless of truncation state. */
  alwaysShowTooltip?: boolean;
  /** Tooltip placement. */
  placement?: TooltipPlacement;
  /** Optional className for the container. */
  className?: string;
  /** Optional inline styles for the container. */
  style?: CSSProperties;
  /** Click handler. Adds pointer cursor automatically. */
  onClick?: (e: React.MouseEvent) => void;
  /** Children override the displayed content (tooltip still uses `text`). */
  children?: ReactNode;
};

/**
 * Single-line text with ellipsis overflow.
 * Shows a tooltip only when the content is actually truncated
 * (or always when `alwaysShowTooltip` is set).
 */
export function TruncatedText({
  text,
  tooltipText,
  maxWidth = 180,
  alwaysShowTooltip = false,
  placement = 'top',
  className,
  style,
  onClick,
  children,
}: TruncatedTextProps) {
  const [isTruncated, setIsTruncated] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const updateTruncation = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setIsTruncated(isTextOverflowing(el));
  }, []);

  useLayoutEffect(() => {
    updateTruncation();
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;

    const ro = new ResizeObserver(() => {
      requestAnimationFrame(updateTruncation);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [text, maxWidth, alwaysShowTooltip, updateTruncation]);

  const resolvedMaxWidth =
    typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth;

  const content = (
    <div
      ref={ref}
      className={className}
      onClick={onClick}
      style={{
        maxWidth: resolvedMaxWidth,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        display: 'block',
        ...(onClick ? { cursor: 'pointer' } : {}),
        ...style,
      }}
    >
      {children ?? text}
    </div>
  );

  const showTooltip = alwaysShowTooltip || isTruncated;
  const title = tooltipText ?? text ?? '';

  // Always keep the TooltipTrigger wrapper in the tree and toggle it via
  // `disabled` instead of conditionally wrapping. Conditional wrapping
  // re-parents the measured div when truncation is detected, which both
  // changes its layout (breaking the measurement that justified the
  // tooltip, so it immediately unwraps again) and detaches the
  // ResizeObserver from the live node.
  return (
    <TooltipTrigger
      text={title}
      placement={placement}
      disabled={!showTooltip || !title}
      className="spark-truncated-text-trigger"
    >
      {content}
    </TooltipTrigger>
  );
}
